/**
 * ObservabilityService — ponto único de contato com o Sentry.
 * Nenhum outro arquivo importa @sentry/vue diretamente.
 * Regra: nunca incluir senhas, tokens, CVVs ou números de cartão.
 */
import * as Sentry from '@sentry/vue';

import FinancialRepository from './repositories/FinancialRepository';
import SalesRepository from './repositories/SalesRepository';
import ContractRepository from './repositories/ContractRepository';
import AccountRepository from './repositories/AccountRepository';
import KycRepository from './repositories/KycRepository';
import IntegrationRepository from './repositories/IntegrationRepository';

// ─── Núcleo ──────────────────────────────────────────────────────────────────

// Dedup: mesmo erro só é enviado uma vez por janela de 5s.
// Evita rafaga de capturas em cadeia (A falha → B captura → C captura → ...).
const DEDUP_TTL_MS = 5000;
const recentCaptures = new Map();

if (typeof window !== 'undefined') {
  setInterval(() => {
    const now = Date.now();
    for (const [key, ts] of recentCaptures) {
      if (now - ts > DEDUP_TTL_MS) recentCaptures.delete(key);
    }
  }, 60000);
}

function isDuplicate(err, domain) {
  const message = err instanceof Error ? err.message : String(err);
  const key = `${domain}:${message}`;
  const now = Date.now();
  if (recentCaptures.has(key) && now - recentCaptures.get(key) < DEDUP_TTL_MS) return true;
  recentCaptures.set(key, now);
  if (recentCaptures.size > 50) {
    for (const [k, ts] of recentCaptures) {
      if (now - ts > DEDUP_TTL_MS) recentCaptures.delete(k);
    }
  }
  return false;
}

/**
 * Classifica o tipo de erro de forma segura.
 * Não lança exceção — sempre retorna uma string.
 *
 * @param {*} err
 * @returns {'timeout'|'network_error'|'server_error'|'client_error'|'unknown'}
 */
function classifyError(err) {
  try {
    if (!err) return 'unknown';
    const status = err?.response?.status || err?.status;
    if (err.code === 'ECONNABORTED' || String(err.message).toLowerCase().includes('timeout')) return 'timeout';
    if (String(err.message).toLowerCase().includes('network error') || err.code === 'ERR_NETWORK') return 'network_error';
    if (status >= 500) return 'server_error';
    if (status >= 400) return 'client_error';
  } catch (_) { /* no-op */ }
  return 'unknown';
}

/**
 * Extrai o HTTP status do erro de forma segura.
 * Suporta axios (err.response.status) e fetch (err.status).
 *
 * @param {*} err
 * @returns {string|undefined}
 */
function extractHttpStatus(err) {
  try {
    const status = err?.response?.status || err?.status;
    if (status && Number.isInteger(Number(status))) return String(status);
  } catch (_) { /* no-op */ }
  return undefined;
}

/**
 * Dispara a captura de exceção enriquecida com o contexto do repositório.
 * Ponto central que toda chamada pública passa por aqui.
 *
 * Enriquecimentos automáticos (adicionados a todos os eventos):
 *   - http_status: código HTTP da resposta (ex: "500", "404")
 *   - error_type: classificação semântica (timeout, network_error, server_error, client_error, unknown)
 *   - fingerprint: agrupa por domínio+flow evitando mistura entre domínios no mesmo issue
 *
 * @param {Error} err
 * @param {{ tags?: object, extra?: object }} repoContext — retorno de um buildXxxContext()
 * @param {object} [customParams] — parâmetros livres adicionais passados pela view
 */
function capture(err, repoContext = {}, customParams = {}) {
  // Observability must never break the app: swallow any Sentry error.
  try {
    const baseTags = repoContext.tags ?? {};
    const domain = baseTags.domain || 'unknown';
    const flow = baseTags.flow || 'unknown';

    // Dedup: não enviar o mesmo erro mais de 1x em 5s.
    if (isDuplicate(err, domain)) return;

    const httpStatus = extractHttpStatus(err);
    const errorType = classifyError(err);

    const tags = {
      ...baseTags,
      error_type: errorType,
      ...(httpStatus ? { http_status: httpStatus } : {}),
    };

    const fingerprint = ['{{ default }}', domain, errorType];

    Sentry.withScope((scope) => {
      Object.entries(tags).forEach(([k, v]) => scope.setTag(k, String(v)));
      // flow sempre como tag: permite filtrar no Discover independente do fingerprint
      scope.setTag('flow', flow);
      scope.setFingerprint(fingerprint);
      scope.setExtra('_params', { ...repoContext.extra, ...customParams });
      scope.captureException(err);
    });
  } catch (_) {
    /* no-op */
  }
}

// ─── API Pública ─────────────────────────────────────────────────────────────

const ObservabilityService = {

  // ── Identidade ──────────────────────────────────────────────────────────────

  /**
   * Seta a identidade do usuário logado no Sentry.
   * Chamar imediatamente após userRequest resolver no store.
   * A partir daqui, TODOS os eventos capturados carregam id, name e email automaticamente.
   *
   * @param {{ id: number|string, name?: string, email?: string }} user
   */
  setUser(user) {
    if (!user?.id) return;
    Sentry.setUser({
      id: String(user.id),
      username: user.name ?? undefined,
      email: user.email ?? undefined,
    });
    // Tag explicita para filtro rapido no Sentry Discover
    Sentry.setTag('seller_id', String(user.id));
  },

  /**
   * Remove a identidade do Sentry.
   * Chamar no logout para não contaminar eventos de sessões futuras.
   */
  clearUser() {
    Sentry.setUser(null);
  },

  // ── Financeiro ──────────────────────────────────────────────────────────────

  /**
   * Falha no fluxo de saque (POST /withdraw).
   * @param {Error} err
   * @param {{ requestedAmount?: number, bankAccountId?: string|number, walletCurrencyId?: number, taxaSaque?: number, currencyConversionUsed?: boolean, [key: string]: any }} ctx
   */
  withdrawFailed(err, ctx = {}) {
    const { requestedAmount, bankAccountId, walletCurrencyId, taxaSaque, currencyConversionUsed, ...custom } = ctx;
    capture(
      err,
      FinancialRepository.buildWithdrawContext({ requestedAmount, bankAccountId, walletCurrencyId, taxaSaque, currencyConversionUsed }),
      custom,
    );
  },

  /**
   * Falha no fluxo de antecipação (POST /antecipation).
   * @param {Error} err
   * @param {{ solicitado?: number, tarifa?: number, antecipado?: number, anticipaTudo?: boolean, currencyId?: number, result?: string, [key: string]: any }} ctx
   */
  anticipationFailed(err, ctx = {}) {
    const { solicitado, tarifa, antecipado, anticipaTudo, currencyId, result, ...custom } = ctx;
    capture(
      err,
      FinancialRepository.buildAnticipationContext({ solicitado, tarifa, antecipado, anticipaTudo, currencyId, result }),
      custom,
    );
  },

  /**
   * Falha ao buscar saldo financeiro (GET /dashboard?types=statements).
   * Distingue "erro de API" de "saldo real zero" via evento explícito no Sentry.
   * @param {Error} err
   * @param {{ currencyId?: number, types?: string[], [key: string]: any }} ctx
   */
  balanceFetchFailed(err, ctx = {}) {
    const { currencyId, types, ...custom } = ctx;
    capture(
      err,
      FinancialRepository.buildBalanceFetchContext({ currencyId, types }),
      custom,
    );
  },

  // ── Vendas ──────────────────────────────────────────────────────────────────

  /**
   * Falha no export de vendas (POST /sale/export).
   * O e-mail do seller não é enviado aqui: já vem da identidade (setUser) para evitar PII duplicada.
   * @param {Error} err
   * @param {{ dadosCount?: number, exportCharges?: boolean, filters?: object, [key: string]: any }} ctx
   */
  saleExportFailed(err, ctx = {}) {
    const { dadosCount, exportCharges, filters, ...custom } = ctx;
    capture(
      err,
      SalesRepository.buildExportContext({ dadosCount, exportCharges, filters }),
      custom,
    );
  },

  /**
   * Falha ao atualizar status de uma venda (PUT /sale/:id).
   * @param {Error} err
   * @param {{ saleId?: number|string, toStatus?: string, [key: string]: any }} ctx
   */
  saleUpdateFailed(err, ctx = {}) {
    const { saleId, toStatus, ...custom } = ctx;
    capture(
      err,
      SalesRepository.buildUpdateContext({ saleId, toStatus }),
      custom,
    );
  },

  /**
   * Falha no relatório de vendas (GET /sale/report).
   * @param {Error} err
   * @param {{ dateRange?: object, filters?: object, typeDate?: string, [key: string]: any }} ctx
   */
  saleReportFailed(err, ctx = {}) {
    const { dateRange, filters, typeDate, ...custom } = ctx;
    capture(
      err,
      SalesRepository.buildReportContext({ dateRange, filters, typeDate }),
      custom,
    );
  },

  // ── Contratos ───────────────────────────────────────────────────────────────

  /**
   * Falha no export de contratos/assinaturas (POST /contract/export blob).
   * @param {Error} err
   * @param {{ filters?: object, dateRange?: object, [key: string]: any }} ctx
   */
  contractExportFailed(err, ctx = {}) {
    const { filters, dateRange, ...custom } = ctx;
    capture(
      err,
      ContractRepository.buildExportContext({ filters, dateRange }),
      custom,
    );
  },

  /**
   * Falha de parse JSON no histórico do contrato (erro silencioso no CSV exportado).
   * @param {Error} err
   * @param {{ contractId?: number|string, [key: string]: any }} ctx
   */
  contractHistoryParseFailed(err, ctx = {}) {
    const { contractId, ...custom } = ctx;
    capture(
      err,
      ContractRepository.buildHistoryParseContext({ contractId }),
      custom,
    );
  },

  // ── Conta ───────────────────────────────────────────────────────────────────

  /**
   * Falha ao atualizar dados do seller (PUT /user/:id).
   * @param {Error} err
   * @param {{ tipo?: 'user'|'company', hasDocuments?: boolean, hasImage?: boolean, [key: string]: any }} ctx
   */
  accountUpdateFailed(err, ctx = {}) {
    const { tipo, hasDocuments, hasImage, ...custom } = ctx;
    capture(
      err,
      AccountRepository.buildUpdateContext({ tipo, hasDocuments, hasImage }),
      custom,
    );
  },

  /**
   * Falha ao buscar configurações de conta do seller (GET /user-settings).
   * @param {Error} err
   * @param {{ setting?: string, [key: string]: any }} ctx
   */
  accountSettingsFetchFailed(err, ctx = {}) {
    const { setting, ...custom } = ctx;
    capture(
      err,
      AccountRepository.buildSettingsFetchContext({ setting }),
      custom,
    );
  },

  // -- KYC (Verificacao de Identidade) ----------------------------------------

  /**
   * Falha ao buscar poses de validacao (POST /validation).
   * @param {Error} err
   * @param {{ step?: string, device?: string, [key: string]: any }} ctx
   */
  kycFetchPosesFailed(err, ctx = {}) {
    const { step, device, ...custom } = ctx;
    capture(
      err,
      KycRepository.buildFetchPosesContext({ step, device }),
      custom,
    );
  },

  /**
   * Falha no upload de video/selfie no fluxo KYC (POST /validation/upload).
   * @param {Error} err
   * @param {{ step?: string, isMac?: boolean, videoType?: string, hasDocFront?: boolean, hasDocBack?: boolean, device?: string, [key: string]: any }} ctx
   */
  kycVideoUploadFailed(err, ctx = {}) {
    const { step, isMac, videoType, hasDocFront, hasDocBack, device, ...custom } = ctx;
    capture(
      err,
      KycRepository.buildVideoUploadContext({ step, isMac, videoType, hasDocFront, hasDocBack, device }),
      custom,
    );
  },

  /**
   * Falha ao salvar imagens do documento no fluxo KYC (PUT /user/:id).
   * @param {Error} err
   * @param {{ step?: string, documentType?: string, hasDocFront?: boolean, hasDocBack?: boolean, device?: string, [key: string]: any }} ctx
   */
  kycSaveImagesFailed(err, ctx = {}) {
    const { step, documentType, hasDocFront, hasDocBack, device, ...custom } = ctx;
    capture(
      err,
      KycRepository.buildSaveImagesContext({ step, documentType, hasDocFront, hasDocBack, device }),
      custom,
    );
  },

  /**
   * Falha ao salvar imagens do documento KYC.
   */
  kycSaveImagesFailed(err, ctx = {}) {
    const { step, documentType, hasDocFront, hasDocBack, device, ...custom } = ctx;
    capture(
      err,
      KycRepository.buildSaveImagesContext({ step, documentType, hasDocFront, hasDocBack, device }),
      custom,
    );
  },
  // kycCameraFailed removido: buildCameraErrorContext não é wired em nenhum componente.
  // Reativar quando o fluxo de câmera nativa do ADM Legado for instrumentado.

  // ── Integrações ─────────────────────────────────────────────────────────────

  /**
   * Falha em fetch de recurso de integração externa (Bitrix, Active Campaign, Circle, Memberkit, VoompPlay).
   * Regra: NUNCA enviar token/url com credencial no ctx.
   * @param {Error} err
   * @param {{ provider?: string, resource?: string, [key: string]: any }} ctx
   */
  integrationFetchFailed(err, ctx = {}) {
    const { provider, resource, ...custom } = ctx;
    capture(
      err,
      IntegrationRepository.buildFetchContext({ provider, resource }),
      custom,
    );
  },
};

export default ObservabilityService;
